import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Fab,
  Tooltip,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  Typography,
} from "@mui/material";
import {
  Close,
  DescriptionOutlined,
  Download,
  HelpOutline,
  OpenInNew,
  Search,
} from "@mui/icons-material";
import { useAuth } from "app/providers/authContext";
import { accessibleModuleGuides } from "./moduleGuides";
import { useHelpPrompt } from "./useHelpPrompt";
import ConciliationFlowGuide from "./conciliationFlow/ConciliationFlowGuide";

export default function ModuleGuides({ onNavigate, floating = false, renderTrigger }) {
  const auth = useAuth();
  const guides = accessibleModuleGuides(auth);
  const showPrompt = useHelpPrompt(floating ? auth.helpPromptExpiresAt : null);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false),
    [query, setQuery] = useState(""),
    [selectedKey, setSelectedKey] = useState(null),
    [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  const allowed = useRef(guides),
    alive = useRef(true),
    locked = useRef(false);
  allowed.current = guides;
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  const selected = guides.find((item) => item.key === selectedKey);
  const current = guides.find((item) => item.route === pathname);
  const search = query.trim().toLocaleLowerCase("es");
  const filtered = guides.filter((item) =>
    `${item.title} ${item.category}`.toLocaleLowerCase("es").includes(search)
  );
  const launch = (key) => {
    setSelectedKey(key || current?.key || guides[0]?.key);
    setQuery("");
    setError("");
    setOpen(true);
  };
  const download = async (diagrams = false) => {
    if (locked.current || !selected || !allowed.current.some((item) => item.key === selected.key))
      return;
    locked.current = true;
    setBusy(true);
    setError("");
    try {
      const createPdf = diagrams
        ? (await import("./conciliationFlow/flowPdf")).createConciliationFlowPdf
        : (await import("./moduleGuidePdf")).createModuleGuidePdf;
      if (!alive.current || !allowed.current.some((item) => item.key === selected.key)) return;
      const content = await createPdf(selected);
      if (!alive.current || !allowed.current.some((item) => item.key === selected.key)) return;
      const url = URL.createObjectURL(new Blob([content], { type: "application/pdf" }));
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `${diagrams ? "diagramas" : "guia"}-${selected.title
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")}.pdf`;
      anchor.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      if (alive.current) setError("No se pudo generar la guía. Vuelve a intentarlo.");
    } finally {
      locked.current = false;
      if (alive.current) setBusy(false);
    }
  };
  if (!guides.length) return null;
  return (
    <>
      {renderTrigger ? (
        renderTrigger(() => launch())
      ) : floating ? (
        showPrompt && (
          <Box
            sx={{
              position: "fixed",
              right: { xs: 16, sm: 28 },
              bottom: { xs: 16, sm: 28 },
              zIndex: (theme) => theme.zIndex.drawer + 1,
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <Typography
              sx={{
                bgcolor: "background.paper",
                color: "text.primary",
                px: 1.5,
                py: 1,
                borderRadius: 2,
                boxShadow: 2,
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              ¿Necesitas ayuda?
            </Typography>
            <Tooltip title="Abre las guías de tus módulos" placement="top">
              <Fab
                aria-label="Abrir guías de uso"
                onClick={() => launch()}
                sx={{
                  "&&": {
                    width: 58,
                    height: 58,
                    bgcolor: "#195b78",
                    color: "#fff",
                    border: "3px solid #fff",
                  },
                  "&&:hover": { bgcolor: "#123f56" },
                }}
              >
                <HelpOutline
                  sx={{ "&&": { fontSize: "30px !important", width: 30, height: 30 } }}
                />
              </Fab>
            </Tooltip>
          </Box>
        )
      ) : (
        <Box sx={{ p: 2, borderRadius: 2, bgcolor: "#edf5fa", border: "1px solid #cbdfe9", mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <DescriptionOutlined sx={{ color: "#195b78" }} />
            <Typography component="h2" sx={{ fontSize: 15, fontWeight: 700, color: "#254459" }}>
              Guías de los módulos
            </Typography>
          </Box>
          <Typography sx={{ fontSize: 12, color: "#526f82", mb: 1.5 }}>
            Consulta cómo usar los módulos habilitados para tu cuenta. Puedes leer cada guía o
            descargarla en PDF.
          </Typography>
          <Button
            fullWidth
            onClick={() => launch()}
            sx={{
              "&&": { bgcolor: "#195b78", color: "#fff", minHeight: 42 },
              "&&:hover": { bgcolor: "#123f56" },
            }}
          >
            Ver guías ({guides.length})
          </Button>
          {current && (
            <Button
              fullWidth
              onClick={() => launch(current.key)}
              sx={{
                "&&": { color: "#195b78", mt: 1, textTransform: "none", whiteSpace: "normal" },
              }}
            >
              Guía de {current.title}
            </Button>
          )}
        </Box>
      )}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth={false}
        PaperProps={{
          sx: {
            borderRadius: 3,
            width: { xs: "calc(100% - 24px)", sm: "calc(100% - 48px)" },
            maxWidth: 1520,
            height: "min(940px, 94dvh)",
            maxHeight: "94dvh",
            m: { xs: 1.5, sm: 3 },
          },
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: "#195b78",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            px: 3,
            py: 2,
          }}
        >
          <DescriptionOutlined />
          <Box sx={{ flex: 1 }}>
            <Typography component="h2" sx={{ fontSize: 20, fontWeight: 700, color: "#fff" }}>
              Guías de uso de Goper
            </Typography>
            <Typography sx={{ fontSize: 12, color: "#d7e9f1" }}>
              Documentación de los módulos habilitados para tu cuenta
            </Typography>
          </Box>
          <IconButton
            aria-label="Cerrar guías"
            onClick={() => setOpen(false)}
            sx={{ color: "#fff" }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            p: 0,
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            overflow: "hidden",
          }}
        >
          <Box
            component="nav"
            aria-label="Guías disponibles"
            sx={{
              width: { xs: "100%", md: 300 },
              flexShrink: 0,
              borderRight: { md: "1px solid" },
              borderColor: "divider",
              bgcolor: "action.hover",
              display: "flex",
              flexDirection: "column",
              maxHeight: { xs: 210, md: "none" },
            }}
          >
            <Box sx={{ p: 2 }}>
              <TextField
                fullWidth
                label="Buscar módulo"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                InputProps={{ startAdornment: <Search sx={{ mr: 1, color: "text.secondary" }} /> }}
                sx={{ "& .MuiInputBase-root": { minHeight: 48, fontSize: 14 } }}
              />
            </Box>
            <List dense sx={{ overflowY: "auto", px: 1, flex: 1 }}>
              {filtered.map((item) => (
                <ListItemButton
                  key={item.key}
                  selected={item.key === selected?.key}
                  onClick={() => {
                    setSelectedKey(item.key);
                    setError("");
                  }}
                  sx={{
                    borderRadius: 1.5,
                    mb: 0.5,
                    "&.Mui-selected": {
                      bgcolor: (theme) => (theme.palette.mode === "dark" ? "#294961" : "#dcebf4"),
                    },
                    "&.Mui-selected:hover": {
                      bgcolor: (theme) => (theme.palette.mode === "dark" ? "#355a75" : "#d0e3ef"),
                    },
                  }}
                >
                  <ListItemText
                    primary={item.title}
                    secondary={item.category}
                    primaryTypographyProps={{
                      fontSize: 14,
                      fontWeight: 600,
                      color:
                        item.key === selected?.key
                          ? (theme) => (theme.palette.mode === "dark" ? "#d4e9ff" : "#195b78")
                          : "text.primary",
                    }}
                    secondaryTypographyProps={{ fontSize: 11 }}
                  />
                </ListItemButton>
              ))}
              {!filtered.length && (
                <Typography sx={{ fontSize: 13, p: 2, color: "text.secondary" }}>
                  No hay guías que coincidan con la búsqueda.
                </Typography>
              )}
            </List>
          </Box>
          <Box
            component="article"
            aria-label={selected ? `Guía de ${selected.title}` : "Documento de ayuda"}
            sx={{ flex: 1, overflowY: "auto", p: { xs: 2, sm: 3 }, minWidth: 0 }}
          >
            {selected ? (
              selected.flow ? (
                <ConciliationFlowGuide />
              ) : (
                <>
                  <Chip
                    size="small"
                    label={selected.category}
                    sx={{
                      mb: 1.5,
                      bgcolor: "#edf5fa",
                      color: "#195b78",
                      maxWidth: "100%",
                      "& .MuiChip-label": { whiteSpace: "normal" },
                      height: "auto",
                      py: 0.5,
                    }}
                  />
                  <Typography
                    component="h3"
                    sx={{ fontSize: 25, fontWeight: 700, mb: 1, color: "text.primary" }}
                  >
                    {selected.title}
                  </Typography>
                  <Typography
                    sx={{ fontSize: 15, lineHeight: 1.7, color: "text.secondary", mb: 3 }}
                  >
                    {selected.purpose}
                  </Typography>
                  <Box sx={{ p: 2, mb: 3, bgcolor: "action.hover", borderRadius: 2 }}>
                    <Typography sx={{ fontSize: 14, lineHeight: 1.75 }}>
                      En una tabla, cada fila reúne un registro y cada columna muestra un dato. ID
                      significa número de referencia. Buscar o filtrar oculta las filas que no
                      coinciden: no las elimina. Los botones disponibles dependen de tus permisos.
                    </Typography>
                  </Box>
                  <Typography component="h4" sx={{ fontSize: 17, fontWeight: 700, mb: 1 }}>
                    Qué información aparece
                  </Typography>
                  <Typography sx={{ fontSize: 14, lineHeight: 1.75, mb: 2 }}>
                    {selected.row}
                  </Typography>
                  <Box component="dl" sx={{ m: 0, mb: 3 }}>
                    {selected.fields.map(([label, description]) => (
                      <Box
                        key={label}
                        sx={{ mb: 2, pl: 2, borderLeft: "3px solid", borderColor: "divider" }}
                      >
                        <Typography component="dt" sx={{ fontSize: 14, fontWeight: 700, mb: 0.5 }}>
                          {label}
                        </Typography>
                        <Typography
                          component="dd"
                          sx={{ m: 0, fontSize: 14, lineHeight: 1.75, color: "text.secondary" }}
                        >
                          {description}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                  <Typography component="h4" sx={{ fontSize: 17, fontWeight: 700, mb: 1 }}>
                    Cómo utilizarlo
                  </Typography>
                  <Box
                    component="ol"
                    sx={{
                      pl: 3,
                      mt: 0,
                      mb: 3,
                      "& li": {
                        pl: 0.5,
                        mb: 1.5,
                        fontSize: 14,
                        lineHeight: 1.75,
                        color: "text.primary",
                      },
                    }}
                  >
                    {selected.steps.map((text) => (
                      <li key={text}>{text}</li>
                    ))}
                  </Box>
                  <Typography component="h4" sx={{ fontSize: 17, fontWeight: 700, mb: 1 }}>
                    Qué pasa cuando usas una acción
                  </Typography>
                  {selected.actions.map(([label, description]) => (
                    <Box
                      key={label}
                      sx={{ p: 2, mb: 1.5, bgcolor: "action.hover", borderRadius: 2 }}
                    >
                      <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 0.5 }}>
                        {label}
                      </Typography>
                      <Typography sx={{ fontSize: 14, lineHeight: 1.75 }}>{description}</Typography>
                    </Box>
                  ))}
                  <Box
                    sx={{
                      my: 3,
                      p: 2,
                      bgcolor: "#edf6f1",
                      border: "1px solid #c8e0d3",
                      borderRadius: 2,
                    }}
                  >
                    <Typography sx={{ fontSize: 15, fontWeight: 700, color: "#24583f", mb: 1 }}>
                      Un ejemplo para orientarte
                    </Typography>
                    <Typography sx={{ fontSize: 14, lineHeight: 1.75, color: "#24583f" }}>
                      {selected.example}
                    </Typography>
                  </Box>
                  <Typography component="h4" sx={{ fontSize: 17, fontWeight: 700, mb: 1 }}>
                    Cómo interpretar la información
                  </Typography>
                  {selected.reading.map((text) => (
                    <Typography key={text} sx={{ fontSize: 14, lineHeight: 1.75, mb: 1.5 }}>
                      {text}
                    </Typography>
                  ))}
                  <Divider sx={{ my: 2.5 }} />
                  <Box sx={{ p: 2, bgcolor: "action.hover", borderRadius: 2 }}>
                    <Typography component="h4" sx={{ fontSize: 15, fontWeight: 700, mb: 1 }}>
                      Antes de confirmar una acción
                    </Typography>
                    {selected.notes.map((text) => (
                      <Typography key={text} sx={{ fontSize: 13, lineHeight: 1.7, mb: 1 }}>
                        {text}
                      </Typography>
                    ))}
                  </Box>
                </>
              )
            ) : (
              <Alert severity="info">
                Selecciona una guía de los módulos que tienes habilitados.
              </Alert>
            )}
          </Box>
        </DialogContent>
        {error && (
          <Alert severity="error" sx={{ mx: 3, mt: 1 }}>
            {error}
          </Alert>
        )}
        <DialogActions
          sx={{ p: 2, borderTop: "1px solid", borderColor: "divider", gap: 1, flexWrap: "wrap" }}
        >
          <Button onClick={() => setOpen(false)} sx={{ "&&": { color: "text.primary" } }}>
            Cerrar
          </Button>
          <Button
            startIcon={busy ? <CircularProgress size={16} /> : <Download />}
            disabled={!selected || busy}
            onClick={() => download()}
            sx={{
              "&&": { color: (theme) => (theme.palette.mode === "dark" ? "#a7d9ff" : "#195b78") },
              "&&.Mui-disabled": { color: "#82939e" },
            }}
          >
            Descargar PDF
          </Button>
          {selected?.flow && (
            <Button
              startIcon={busy ? <CircularProgress size={16} /> : <Download />}
              disabled={busy}
              onClick={() => download(true)}
              sx={{ "&&": { bgcolor: "#195b78", color: "#fff" } }}
            >
              Descargar diagramas
            </Button>
          )}
          {!selected?.flow && (
            <Button
              startIcon={<OpenInNew />}
              disabled={!selected}
              onClick={() => {
                if (!selected || !allowed.current.some((item) => item.key === selected.key)) return;
                setOpen(false);
                onNavigate?.();
                navigate(selected.route);
              }}
              sx={{
                "&&": { bgcolor: "#195b78", color: "#fff" },
                "&&.Mui-disabled": { bgcolor: "#e2e9ee", color: "#82939e" },
              }}
            >
              Ir al módulo
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}

ModuleGuides.propTypes = {
  onNavigate: PropTypes.func,
  floating: PropTypes.bool,
  renderTrigger: PropTypes.func,
};
